import ItinerarySection from './ItinerarySection';
import PdfPreview from './PdfPreview';
import ShowReminderSettings from './ShowReminderSettings';
import ShowTasksSection from './ShowTasksSection';
import { useTranslation } from 'react-i18next';
import type { VenueDTO } from '../api';
import { countryToTimezone } from '../lib/countryTimezone';

/**
 * DayTab — the "Day" tab of the Show detail page: the day-sheet (itinerary,
 * roadmap, schedule) editor plus the generated daysheet PDF download/preview.
 * Also hosts the show-scoped Daysheet reminder settings and the per-show Tasks
 * list (both moved/integrated into the show here — no separate menu).
 *
 * Extracted from ShowDetailPage (2026-08-14, Vague 3 pass 6). Props-driven.
 */
export interface DayTabProps {
  form: Record<string, any>;
  setForm: (patch: Record<string, any>) => void;
  realId: string | null;
  exportDaysheet: () => void;
  exporting: boolean;
  venue: VenueDTO | null;
  bandCountry: string;
  accommodationCountry: string;
  /** Ville de base du groupe (Band Settings) — LOT 4 (2026-11-04) : distingue un
   *  travel RETOUR (J4) d'un travel aller pour le filtrage des champs. */
  bandHomeCity?: string;
  daysheetUrl: string | null;
  daysheetDoc: any;
  /** The show's band id (form.band_id) — used for the reminder/task member lists. */
  bandId: string;
  /** Whether the current user can mutate this show (editor+ on its band). */
  canWrite: boolean;
}

export default function DayTab({
  form, setForm, realId, exportDaysheet, exporting, venue, bandCountry, accommodationCountry,
  daysheetUrl, daysheetDoc, bandId, canWrite, bandHomeCity,
}: DayTabProps) {
  const { t } = useTranslation();
  // The venue's IANA timezone: the show's own timezone field, else derived from
  // the venue country (same source of truth as ItinerarySection). Feeds the
  // reminder card's "server time · venue local time" line.
  const venueTz = (form as any)?.timezone || countryToTimezone(venue?.country) || null;
  return (
    <>
      <ItinerarySection
        show={form}
        realId={realId}
        onDayChange={(patch) => setForm(patch)}
        onExportDaysheet={exportDaysheet}
        exporting={exporting}
        venue={venue}
        bandCountry={bandCountry}
        accommodationCountry={accommodationCountry}
        bandHomeCity={bandHomeCity}
      />
      <ShowReminderSettings showId={realId} canWrite={canWrite} bandId={bandId} venueTz={venueTz} showDate={(form as any)?.date ?? null} />
      <ShowTasksSection showId={realId} bandId={bandId} canWrite={canWrite} />
      {daysheetUrl && (
        <div data-daytab="pdf" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <a className="btn btn-primary" href={daysheetUrl} download={`daysheet-${(form.name || '').replace(/[^a-z0-9-_]+/gi, '-') || realId}.pdf`} style={{ textDecoration: 'none' }}>
              💾 {t('dayTab.savePdf')}
            </a>
          </div>
          {daysheetDoc && <PdfPreview docDef={daysheetDoc} height={480} />}
        </div>
      )}
    </>
  );
}
